import { Suspense, lazy, useEffect } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import ScrollToTop from '@/components/ScrollToTop';
import CartDrawer from '@/components/CartDrawer';
import PageTransition from '@/components/PageTransition';
import { ChunkErrorBoundary } from '@/components/ChunkErrorBoundary';
import AuthDialogMount from '@/components/auth/AuthDialogMount';
import { useLenis } from '@/hooks/useLenis';
import { useScrollLockGuard } from '@/hooks/useScrollLockGuard';
import { useWishlistSync } from '@/hooks/useWishlistSync';
import { trackPageView } from '@/lib/analytics';
import '@/i18n';

const Index = lazy(() => import('./pages/Index'));
const CookieConsent = lazy(() => import('@/components/CookieConsent'));
const EmailCapturePopup = lazy(() => import('@/components/EmailCapturePopup'));

const Collection = lazy(() => import('./pages/Collection'));
const BundleSale = lazy(() => import('./pages/BundleSale'));
const BundleDeal = lazy(() => import('./pages/BundleDeal'));
const Product = lazy(() => import('./pages/Product'));
const SearchPage = lazy(() => import('./pages/Search'));
const NotFound = lazy(() => import('./pages/NotFound'));
const About = lazy(() => import('./pages/About'));

const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const AccountLayout = lazy(() => import('./pages/account/AccountLayout'));
const AccountDashboard = lazy(() => import('./pages/account/Dashboard'));
const AccountOrders = lazy(() => import('./pages/account/Orders'));
const AccountOrderDetail = lazy(() => import('./pages/account/OrderDetail'));
const AccountInvoice = lazy(() => import('./pages/account/Invoice'));
const AccountWishlist = lazy(() => import('./pages/account/Wishlist'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const AccountAddresses = lazy(() => import('./pages/account/Addresses'));
const AccountSettings = lazy(() => import('./pages/account/Settings'));
const AccountPrivacy = lazy(() => import('./pages/account/Privacy'));
const AccountGiftCards = lazy(() => import('./pages/account/GiftCards'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));

const Shipping = lazy(() => import('./pages/legal/Shipping'));
const Returns = lazy(() => import('./pages/legal/Returns'));
const OrderCancellation = lazy(() => import('./pages/legal/OrderCancellation'));
const Contact = lazy(() => import('./pages/legal/Contact'));
const Impressum = lazy(() => import('./pages/legal/Impressum'));
const Privacy = lazy(() => import('./pages/legal/Privacy'));
const Terms = lazy(() => import('./pages/legal/Terms'));
const Cookies = lazy(() => import('./pages/legal/Cookies'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'bloom-rq-cache-v2',
  throttleTime: 1000,
});

// Bust on each build so a deploy doesn't serve stale shapes.
const buster =
  (import.meta.env.VITE_BUILD_ID as string | undefined) ??
  (import.meta.env.MODE === 'production' ? 'prod' : 'dev');

function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function AppShell() {
  useLenis();
  useScrollLockGuard();
  useWishlistSync();

  // Meta Pixel SPA PageView on every route change. The base script fires the
  // initial PageView on init; this covers client-side navigations.
  const { pathname, search } = useLocation();
  useEffect(() => {
    trackPageView();
  }, [pathname, search]);


  return (
    <>
      <ScrollToTop />
      <CartDrawer />
      <AuthDialogMount />
      {/* Global popups — mounted once so they appear no matter which page the
          visitor lands on directly. Do NOT move these into Layout. */}
      <Suspense fallback={null}>
        <CookieConsent />
        <EmailCapturePopup />
      </Suspense>
      <ChunkErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <PageTransition>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/collections" element={<Navigate to="/collections/new" replace />} />
              <Route path="/collections/sale" element={<BundleSale />} />
              <Route path="/collections/sale/:bundleId" element={<BundleDeal />} />
              <Route path="/collections/:slug" element={<Collection />} />
              <Route path="/product/:handle" element={<Product />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/wishlist" element={<WishlistPage />} />
              <Route path="/account" element={<AccountLayout />}>
                <Route index element={<AccountDashboard />} />
                <Route path="orders" element={<AccountOrders />} />
                <Route path="orders/:orderId" element={<AccountOrderDetail />} />
                <Route path="orders/:orderId/invoice" element={<AccountInvoice />} />
                <Route path="wishlist" element={<AccountWishlist />} />
                <Route path="addresses" element={<AccountAddresses />} />
                <Route path="settings" element={<AccountSettings />} />
                <Route path="privacy" element={<AccountPrivacy />} />
                <Route path="gift-cards" element={<AccountGiftCards />} />
              </Route>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
              </Route>
              <Route path="/shipping" element={<Shipping />} />
              <Route path="/returns" element={<Returns />} />
              <Route path="/order-cancellation" element={<OrderCancellation />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/about" element={<About />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </PageTransition>
        </Suspense>
      </ChunkErrorBoundary>
    </>
  );
}

const App = () => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: 24 * 60 * 60 * 1000,
      buster,
      dehydrateOptions: {
        // Only persist Shopify queries (lists + product details). Skip everything else.
        shouldDehydrateQuery: () => false,
      },
    }}
  >
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </TooltipProvider>
  </PersistQueryClientProvider>
);

export default App;
